/* eslint-disable @typescript-eslint/no-explicit-any */
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { EncryptionService } from './encryption.service';
import { assets } from '../core/constants/assets.const';
import { environment } from '../../environments/environment';
import { isPlatformBrowser } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class MessagingService {
  assetsConst = assets;

  private receivedMessageIds = new Set<string>();

  // ✅ RxJS BehaviorSubject instead of Angular signal
  private _message = new BehaviorSubject<any | null>(null);
  readonly currentMessage: Observable<any | null> =
    this._message.asObservable();

  constructor(
    private encryption: EncryptionService,
    private translate: TranslateService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  /**
   * Initialize Firebase safely
   */
  private async getFirebaseMessaging() {
    const { initializeApp, getApps } = await import('firebase/app');
    const { getMessaging } = await import('firebase/messaging');

    const app = getApps().length
      ? getApps()[0]
      : initializeApp(environment.firebaseConfig);

    return getMessaging(app);
  }

  /**
   * Request notification permission
   */
  async requestPermission(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    const { getToken } = await import('firebase/messaging');
    const messaging = await this.getFirebaseMessaging();

    navigator.serviceWorker.ready
      .then(registration =>
        getToken(messaging, {
          vapidKey: environment.firebaseConfig.vapidKey,
          serviceWorkerRegistration: registration,
        })
      )
      .then(currentToken => {
        if (currentToken) {
          this.encryption.encryptLocalStorage(currentToken, 'fcmToken');
        } else {
          alert(this.translate.instant('fcmNotification'));
        }
      })
      .catch(err => console.log('Token error:', err));
  }

  /**
   * Listen for foreground messages
   */
  async listenForFrontMessages(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    const { onMessage } = await import('firebase/messaging');
    const messaging = await this.getFirebaseMessaging();

    if (localStorage[environment.storageKey]) {
      onMessage(messaging, payload => {
        const messageId = payload.messageId;

        if (!messageId) {
          return;
        }

        if (this.receivedMessageIds.has(messageId)) {
          return;
        }

        this.receivedMessageIds.add(messageId);

        if (!payload.data) {
          return;
        }

        const parsedData = this.parseNotificationData(payload.data);

        // System/OS default notification sound plays via showNotification
        // (do not set silent: true).
        const notificationOptions: NotificationOptions = {
          body: parsedData['body'] || payload.data['body'] || 'Click to view',
          icon: this.assetsConst.logo,
          data: parsedData,
        };

        this.displayNotification(
          parsedData['title'] || payload.data['title'] || 'New Notification',
          notificationOptions
        );

        this._message.next({ action: 'refresh', data: parsedData });
      });
    }
  }

  private parseNotificationData(
    data: Record<string, any> | undefined
  ): Record<string, any> {
    if (!data) {
      return {};
    }

    let payloadData: Record<string, any> = {};

    try {
      if (data['payload']) {
        payloadData =
          typeof data['payload'] === 'string'
            ? JSON.parse(data['payload'])
            : data['payload'];
      } else {
        payloadData = data;
      }
    } catch (error) {
      payloadData = data;
    }

    return payloadData;
  }

  private displayNotification(
    title: string,
    options: NotificationOptions
  ): void {
    const currentLang = this.translate.currentLang || 'en';
    const isRtl = this.isRtlLanguage(currentLang);

    options = {
      ...options,
      dir: isRtl ? 'rtl' : 'ltr',
    };

    navigator.serviceWorker
      .getRegistration('/firebase-cloud-messaging-push-scope')
      .then(registration => {
        if (registration) {
          registration.showNotification(title, options);
        }
      });
  }

  private isRtlLanguage(lang: string): boolean {
    return ['ar'].includes(lang);
  }

  sendMessage(message: any) {
    this._message.next(message);
  }
}
